import {useThemeColors} from '@nfq/colors';

import {isDefinedAndPopulated} from 'GlobalShared/utils/typeGuards';
import {setLineBreaks} from 'UI/utils/helpers';

import type {ImageUpload} from 'types/payload-types';

/**
 * The `useFeaturesBig` hook extracts icon metadata, processes subline text for line breaks, and provides theme colors for the FeaturesBig block.
 * It checks whether the provided icon contains a populated image and, if so, extracts its alt text, URL, width, and height for rendering.
 * Additionally, it parses the raw subline string into an array of text rows split at line break markers to support multi-line display.
 *
 * @param icon       The optional image upload object containing the feature icon with its image media data and alt text.
 * @param sublineRaw The optional raw subline string that may contain line break markers to be split into separate rows.
 * @returns An object containing `colors` from the current theme palette, `iconAlt`, `iconHeight`, `iconUrl`, `iconWidth` for the icon image, and `sublineRows` as an array of text lines.
 *
 * @example
 * ```tsx
 * const {colors, iconAlt, iconHeight, iconUrl, iconWidth, sublineRows} = useFeaturesBig(icon, subline);
 * ```
 */
export const useFeaturesBig = (
    icon?: ImageUpload,
    sublineRaw?: string
) => {
    const colors = useThemeColors();
    const sublineRows = setLineBreaks(sublineRaw ?? '');
    let iconAlt;
    let iconUrl;
    let iconWidth;
    let iconHeight;

    if (isDefinedAndPopulated(icon?.image)) {
        iconAlt = icon.alt;
        iconUrl = icon.image.url;
        iconWidth = icon.image.width;
        iconHeight = icon.image.height;
    }

    return {
        colors,
        iconAlt,
        iconHeight,
        iconUrl,
        iconWidth,
        sublineRows
    };
};